import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './App.css';
import { reportClientError } from './utils/clientLogger';
import ChatWidget from './components/home/ChatWidget';
import BookingModal from './components/home/BookingModal';
import ServiceModal from './components/home/ServiceModal';
import { CorporateNavbar } from './components/home/corporate/CorporateNavbar';
import { CorporateHeroSection } from './components/home/corporate/CorporateHeroSection';
import { CorporateServicesSection } from './components/home/corporate/CorporateServicesSection';
import { CorporateTeamSection } from './components/home/corporate/CorporateTeamSection';
import { CorporateClientsSection } from './components/home/corporate/CorporateClientsSection';
import { CorporateContactSection } from './components/home/corporate/CorporateContactSection';
import { CorporateFooter } from './components/home/corporate/CorporateFooter';
import { LANGUAGES, buildServices, buildTeamMembers, CLIENTS } from './data/homeContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_CREDIT_FORM_URL = 'https://form.jotform.com/251332360319045';

function App() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const isPortuguese = i18n.language === 'pt';
  const services = useMemo(() => buildServices(t, AUTO_CREDIT_FORM_URL), [t, i18n.language]);
  const team = useMemo(() => buildTeamMembers(t), [t, i18n.language]);

  const changeLanguage = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setLangMenuOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      reportClientError('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Erro ao enviar mensagem. Tente novamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleServiceAction = (service) => {
    if (service.externalLink) {
      window.open(service.externalLink, '_blank', 'noopener,noreferrer');
      return;
    }

    setSelectedService(service);
  };

  return (
    <div className="rh-premium-app min-h-screen bg-white" data-testid="home-main-page">
      <CorporateNavbar
        t={t}
        i18n={i18n}
        languages={LANGUAGES}
        isPortuguese={isPortuguese}
        mobileMenuOpen={mobileMenuOpen}
        langMenuOpen={langMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        setLangMenuOpen={setLangMenuOpen}
        onChangeLanguage={changeLanguage}
        onScrollSection={scrollToSection}
      />

      <CorporateHeroSection
        isPortuguese={isPortuguese}
        onOpenBooking={() => setBookingModalOpen(true)}
        onViewServices={() => scrollToSection('services')}
        onOpenContact={() => scrollToSection('contact')}
      />

      <CorporateServicesSection t={t} services={services} onServiceAction={handleServiceAction} />
      <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />
      <CorporateTeamSection t={t} team={team} />
      <CorporateClientsSection t={t} clients={CLIENTS} />

      <CorporateContactSection
        t={t}
        formData={formData}
        isSubmitting={isSubmitting}
        submitStatus={submitStatus}
        onInputChange={handleInputChange}
        onSubmit={handleContactSubmit}
      />

      <ChatWidget />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
      <CorporateFooter t={t} />
    </div>
  );
}

export default App;
